import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  developerApi,
  type ActivityEntry,
  type APIAnalyticsSummary,
  type DeveloperApplication,
  type DeveloperDashboard,
  type DeveloperWebhook,
  type ExtensionRecord,
  type MarketplaceApp,
  type PublicAPIEndpoint,
  type SandboxEnvironment,
  type SDKPackage,
  type Scope,
  type StreamEvent,
} from '../../api/developer';

type Tab = 'overview' | 'applications' | 'apis' | 'webhooks' | 'extensions' | 'marketplace' | 'sdks' | 'sandbox' | 'analytics';

export function DeveloperPortalPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<DeveloperDashboard | null>(null);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [apis, setApis] = useState<PublicAPIEndpoint[]>([]);
  const [applications, setApplications] = useState<DeveloperApplication[]>([]);
  const [webhooks, setWebhooks] = useState<DeveloperWebhook[]>([]);
  const [extensions, setExtensions] = useState<ExtensionRecord[]>([]);
  const [marketplace, setMarketplace] = useState<MarketplaceApp[]>([]);
  const [sdks, setSdks] = useState<SDKPackage[]>([]);
  const [sandbox, setSandbox] = useState<SandboxEnvironment | null>(null);
  const [analytics, setAnalytics] = useState<APIAnalyticsSummary | null>(null);
  const [events, setEvents] = useState<StreamEvent[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newAppName, setNewAppName] = useState('My Integration App');

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, sc, apiList, apps, wh, ext, mkt, sdkList, sb, an, ev, act] = await Promise.all([
        developerApi.dashboard(),
        developerApi.listScopes(),
        developerApi.publicAPIs(),
        developerApi.listApplications(),
        developerApi.listWebhooks(),
        developerApi.listExtensions(),
        developerApi.marketplace(),
        developerApi.sdks(),
        developerApi.sandbox(),
        developerApi.analytics(),
        developerApi.listEvents(),
        developerApi.listActivity(),
      ]);
      setDashboard(dash);
      setScopes(sc);
      setApis(apiList);
      setApplications(apps);
      setWebhooks(wh);
      setExtensions(ext);
      setMarketplace(mkt);
      setSdks(sdkList);
      setSandbox(sb);
      setAnalytics(an);
      setEvents(ev);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load developer portal');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const registerApp = async () => {
    setBusy(true);
    try {
      await developerApi.registerApplication({
        name: newAppName,
        scopes: ['catalog.read', 'orders.read'],
        auth_method: 'oauth2',
      });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const registerWebhook = async () => {
    setBusy(true);
    try {
      await developerApi.registerWebhook({
        url: 'https://example.com/webhooks/ai-cos',
        events: ['product.created', 'order.received'],
      });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const installFeatured = async (appId: string) => {
    setBusy(true);
    try {
      await developerApi.installApp({ app_id: appId, workspace_id: (getWorkspaceId() ?? '') });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'applications', label: 'Applications' },
    { id: 'apis', label: 'API Explorer' },
    { id: 'webhooks', label: 'Webhooks' },
    { id: 'extensions', label: 'Extensions' },
    { id: 'marketplace', label: 'Marketplace' },
    { id: 'sdks', label: 'SDKs' },
    { id: 'sandbox', label: 'Sandbox' },
    { id: 'analytics', label: 'Analytics' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Developer Portal</h1>
          <p className="text-sm text-slate-500">
            Public APIs, OAuth apps, webhooks, extensions, SDKs &amp; marketplace — FCP-00012
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Applications', value: dashboard.applications },
            { label: 'Active API Keys', value: dashboard.active_api_keys },
            { label: 'Webhooks', value: dashboard.webhooks },
            { label: 'API Calls (24h)', value: dashboard.api_calls_24h.toLocaleString() },
            { label: 'Extensions', value: dashboard.extensions },
            { label: 'Marketplace Installs', value: dashboard.marketplace_installs },
            { label: 'Error Rate', value: `${(dashboard.error_rate_24h * 100).toFixed(1)}%` },
            { label: 'Rate Limit Used', value: `${dashboard.rate_limit_used_pct}%` },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'applications' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <div>
              <label className="block text-xs font-medium text-slate-500">Application Name</label>
              <input
                value={newAppName}
                onChange={(e) => setNewAppName(e.target.value)}
                className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={registerApp}
              disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Register Application
            </button>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Client ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Scopes</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium">{app.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{app.client_id}</td>
                    <td className="px-4 py-3">{app.status}</td>
                    <td className="px-4 py-3 text-xs">{app.scopes?.join(', ') || '—'}</td>
                  </tr>
                ))}
                {applications.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                      No applications registered yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'apis' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{scopes.length} permission scopes · {apis.length} public API endpoints</p>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Path</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Scopes</th>
                </tr>
              </thead>
              <tbody>
                {apis.slice(0, 30).map((api, i) => (
                  <tr key={`${api.path}-${i}`} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono text-xs">{api.path}</td>
                    <td className="px-4 py-2">{api.method}</td>
                    <td className="px-4 py-2">{api.category}</td>
                    <td className="px-4 py-2 text-xs">{api.scopes?.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'webhooks' && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={registerWebhook}
            disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Register Webhook
          </button>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">URL</th>
                  <th className="px-4 py-3">Events</th>
                  <th className="px-4 py-3">Deliveries</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((wh) => (
                  <tr key={wh.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-mono text-xs">{wh.url}</td>
                    <td className="px-4 py-3 text-xs">{wh.events?.join(', ')}</td>
                    <td className="px-4 py-3">{wh.delivery_count}</td>
                    <td className="px-4 py-3">{wh.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {events.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Recent Events</h3>
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                {events.slice(0, 5).map((e) => (
                  <li key={e.id}>
                    {e.type} — {e.source}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'extensions' && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">Sandboxed</th>
              </tr>
            </thead>
            <tbody>
              {extensions.map((ext) => (
                <tr key={ext.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">{ext.name}</td>
                  <td className="px-4 py-3">{ext.type}</td>
                  <td className="px-4 py-3">{ext.version}</td>
                  <td className="px-4 py-3">{ext.sandboxed ? 'Yes' : 'No'}</td>
                </tr>
              ))}
              {extensions.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                    No extensions registered
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'marketplace' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {marketplace.map((app) => (
            <div key={app.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <h3 className="font-semibold text-slate-900">{app.name}</h3>
                {app.featured && (
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Featured</span>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">{app.vendor} · v{app.version}</p>
              <p className="mt-2 text-sm text-slate-600">{app.description}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span>★ {app.rating?.toFixed(1)} · {app.installs?.toLocaleString()} installs</span>
                <button
                  type="button"
                  onClick={() => installFeatured(app.id)}
                  disabled={busy}
                  className="rounded bg-indigo-600 px-2 py-1 text-white hover:bg-indigo-700"
                >
                  Install
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'sdks' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sdks.map((sdk) => (
            <div key={sdk.language} className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="font-semibold capitalize text-slate-900">{sdk.language}</h3>
              <p className="text-sm text-slate-500">v{sdk.version}</p>
              <p className="mt-2 text-xs text-slate-600">{sdk.changelog}</p>
              {sdk.download_url && (
                <a href={sdk.download_url} className="mt-3 inline-block text-sm text-indigo-600 hover:underline">
                  Download SDK
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'sandbox' && sandbox && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <p className="text-sm text-slate-600">
            Sandbox base URL: <code className="rounded bg-slate-100 px-1">{sandbox.base_url}</code>
          </p>
          <p className="mt-2 text-sm">Mock services: {sandbox.mock_services?.join(', ')}</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {Object.entries(sandbox.test_data_sets ?? {}).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span className="font-medium capitalize">{k}</span>: {v}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'analytics' && analytics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total Calls', value: analytics.total_calls.toLocaleString() },
            { label: 'Error Rate', value: `${analytics.error_rate.toFixed(2)}%` },
            { label: 'Avg Latency', value: `${analytics.avg_latency_ms}ms` },
            { label: 'P95 Latency', value: `${analytics.p95_latency_ms}ms` },
            { label: 'Rate Limit Hits', value: analytics.rate_limit_hits },
            { label: 'Webhook Deliveries', value: analytics.webhook_deliveries },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-xl font-semibold">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {activity.length > 0 && tab === 'overview' && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
          <ul className="mt-2 space-y-2 text-sm text-slate-600">
            {activity.slice(0, 8).map((a) => (
              <li key={a.id}>
                <span className="font-medium">{a.action}</span> — {a.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
